import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Message from "../components/Message";
import Loader from "../components/Loader";
import { getUserDetails, updateUser } from "../actions/userActions";
import { useNavigate, Link } from "react-router-dom";
import { USER_UPDATE_RESET } from "../constants/userConstants";

// Format ISO timestamps the same way the orders panel does so the page
// has one consistent "voice" for dates.
const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString("es-MX", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const UserEditScreen = () => {
  const { id } = useParams();
  const userId = id;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [localMessage, setLocalMessage] = useState(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const userDetails = useSelector((state) => state.userDetails);
  const { loading, error, user } = userDetails;

  const userUpdate = useSelector((state) => state.userUpdate);
  const {
    loading: loadingUpdate,
    error: errorUpdate,
    success: successUpdate,
  } = userUpdate;

  useEffect(() => {
    if (successUpdate) {
      dispatch({ type: USER_UPDATE_RESET });
      navigate("/admin/userlist");
    } else {
      if (!user.name || user._id !== userId) {
        dispatch(getUserDetails(userId));
      } else {
        setName(user.name);
        setEmail(user.email);
        setIsAdmin(user.isAdmin);
        setConfirmed(!!user.confirmed);
      }
    }
  }, [dispatch, user, userId, successUpdate, navigate]);

  const submitHandler = (e) => {
    e.preventDefault();
    setLocalMessage(null);
    if (password && password !== passwordConfirm) {
      setLocalMessage("Las contraseñas no coinciden.");
      return;
    }
    if (password && password.length < 6) {
      setLocalMessage("La contraseña debe tener al menos 6 caracteres.");
      return;
    }

    const payload = { _id: userId, name, email, isAdmin, confirmed };
    if (password) payload.password = password;
    dispatch(updateUser(payload));
  };

  return (
    <div className="hadacy-user-edit">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600&family=Jost:wght@200;300;400;500&display=swap');

        /* ─── HADACY · admin user-edit ───
           Same palette + form pattern as the customer-facing
           ProfileScreen. The right column shows read-only metadata so
           the admin can see at a glance whether the welcome email was
           delivered, when the account was created, etc. */
        .hadacy-user-edit {
          --cream: #f5f0e8;
          --dark: #1a1410;
          --gold: #b8933a;
          --gold-light: #d4af6a;
          --muted: #7a6e62;
          --card-bg: #faf7f2;
          --recess: #efe7d8;
          font-family: "Jost", sans-serif;
          max-width: 960px;
          margin: 0 auto;
        }
        .hadacy-user-edit__back {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.7rem;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--muted);
          text-decoration: none;
          margin-bottom: 1rem;
          transition: color .2s ease;
        }
        .hadacy-user-edit__back:hover { color: var(--gold); }
        .hadacy-user-edit__heading {
          font-family: "Cormorant Garamond", serif;
          font-weight: 400;
          letter-spacing: 0.04em;
          color: var(--dark);
          font-size: 2rem;
          margin: 0 0 0.25rem 0;
        }
        .hadacy-user-edit__subheading {
          font-size: 0.78rem;
          letter-spacing: 0.06em;
          color: var(--muted);
          margin: 0 0 1.6rem 0;
        }

        .hadacy-user-edit__grid {
          display: grid;
          grid-template-columns: 1.4fr 1fr;
          gap: 1.4rem;
        }
        @media (max-width: 768px) {
          .hadacy-user-edit__grid { grid-template-columns: 1fr; }
        }

        .hadacy-user-edit__card {
          background: var(--card-bg);
          border: 1px solid rgba(184, 147, 58, 0.18);
          border-radius: 14px;
          padding: 1.4rem 1.5rem;
        }
        .hadacy-user-edit__card-title {
          font-family: "Cormorant Garamond", serif;
          font-size: 1.3rem;
          font-weight: 400;
          color: var(--dark);
          margin: 0 0 1rem 0;
          padding-bottom: 0.55rem;
          border-bottom: 1px solid rgba(184, 147, 58, 0.18);
          letter-spacing: 0.02em;
        }

        .hadacy-user-edit .form-group { margin-bottom: 1rem; }
        .hadacy-user-edit .form-label,
        .hadacy-user-edit label {
          display: block;
          font-size: 0.62rem;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--muted) !important;
          font-weight: 400;
          margin-bottom: 0.4rem;
        }
        .hadacy-user-edit .form-control {
          background: #fff !important;
          border: 1px solid rgba(184, 147, 58, 0.22) !important;
          border-radius: 10px !important;
          padding: 0.65rem 0.95rem !important;
          font-family: "Jost", sans-serif;
          font-size: 0.92rem;
          color: var(--dark) !important;
          box-shadow: none !important;
          transition: border-color .2s ease, box-shadow .2s ease,
                      background .2s ease;
        }
        .hadacy-user-edit .form-control:focus {
          border-color: var(--gold) !important;
          box-shadow: 0 0 0 3px rgba(184, 147, 58, 0.14) !important;
        }

        /* Switch-style toggles */
        .hadacy-user-edit__toggles {
          display: flex;
          flex-wrap: wrap;
          gap: 0.6rem 1rem;
          margin: 0.4rem 0 1rem 0;
        }
        .hue-toggle {
          display: inline-flex;
          align-items: center;
          gap: 0.55rem;
          padding: 0.55rem 0.95rem;
          border-radius: 999px;
          border: 1px solid rgba(184, 147, 58, 0.22);
          background: #fff;
          cursor: pointer;
          user-select: none;
          font-size: 0.78rem;
          color: var(--dark);
          transition: background .2s ease, border-color .2s ease, color .2s ease;
        }
        .hue-toggle__dot {
          width: 10px; height: 10px; border-radius: 50%;
          background: rgba(184, 147, 58, 0.35);
          box-shadow: inset 0 0 0 2px var(--card-bg);
        }
        .hue-toggle--on {
          background: rgba(184, 147, 58, 0.10);
          border-color: var(--gold);
          color: var(--gold);
        }
        .hue-toggle--on .hue-toggle__dot { background: var(--gold); }
        .hue-toggle input { display: none; }

        /* Password section uses a soft warning cue so the admin
           understands they're about to write a sensitive change. */
        .hadacy-user-edit__danger-zone {
          background: rgba(184, 147, 58, 0.06);
          border: 1px dashed rgba(184, 147, 58, 0.38);
          border-radius: 10px;
          padding: 1rem 1.1rem;
          margin-top: 0.6rem;
        }
        .hadacy-user-edit__danger-zone-title {
          font-size: 0.7rem;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--gold);
          margin: 0 0 0.6rem 0;
        }
        .hadacy-user-edit__danger-zone-help {
          font-size: 0.78rem;
          color: var(--muted);
          margin: 0 0 0.7rem 0;
          line-height: 1.5;
        }

        .hadacy-user-edit__cta {
          display: inline-flex;
          align-items: center;
          gap: 0.55rem;
          padding: 0.7rem 1.5rem;
          border-radius: 999px !important;
          background: var(--dark) !important;
          border: 1px solid var(--dark) !important;
          color: var(--gold-light) !important;
          font-size: 0.7rem;
          font-weight: 400;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          margin-top: 0.4rem;
          cursor: pointer;
          transition: background .2s ease, color .2s ease, border-color .2s ease;
        }
        .hadacy-user-edit__cta:hover,
        .hadacy-user-edit__cta:focus {
          background: var(--gold) !important;
          border-color: var(--gold) !important;
          color: var(--dark) !important;
          box-shadow: none !important;
        }

        /* Read-only metadata column */
        .hadacy-user-edit__meta-row {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          gap: 0.8rem;
          padding: 0.55rem 0;
          border-bottom: 1px dashed rgba(184, 147, 58, 0.22);
          font-size: 0.85rem;
        }
        .hadacy-user-edit__meta-row:last-child { border-bottom: none; }
        .hadacy-user-edit__meta-label {
          font-size: 0.62rem;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--muted);
        }
        .hadacy-user-edit__meta-value {
          color: var(--dark);
          font-size: 0.85rem;
          text-align: right;
        }
        .hadacy-user-edit__meta-value--ok { color: var(--gold); }
        .hadacy-user-edit__meta-value--err { color: #b04a3c; }
        .hadacy-user-edit__meta-error {
          font-size: 0.78rem;
          color: #b04a3c;
          margin-top: 0.6rem;
          background: rgba(176, 74, 60, 0.08);
          border: 1px solid rgba(176, 74, 60, 0.25);
          border-radius: 8px;
          padding: 0.55rem 0.7rem;
          word-break: break-word;
        }
      `}</style>

      <Link to="/admin/userlist" className="hadacy-user-edit__back">
        <i className="fas fa-arrow-left"></i> Volver al listado
      </Link>

      <h1 className="hadacy-user-edit__heading">Editar usuario</h1>
      <p className="hadacy-user-edit__subheading">
        Cualquier cambio quedará registrado en la bitácora administrativa.
      </p>

      {loadingUpdate && <Loader />}
      {errorUpdate && <Message variant="danger">{errorUpdate}</Message>}
      {localMessage && <Message variant="danger">{localMessage}</Message>}

      {loading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error}</Message>
      ) : (
        <div className="hadacy-user-edit__grid">
          {/* ── Editable fields ── */}
          <div className="hadacy-user-edit__card">
            <h2 className="hadacy-user-edit__card-title">Datos de la cuenta</h2>
            <Form onSubmit={submitHandler}>
              <Form.Group controlId="name" className="form-group">
                <Form.Label>Nombre</Form.Label>
                <Form.Control
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Form.Group>

              <Form.Group controlId="email" className="form-group">
                <Form.Label>Correo electrónico</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Form.Group>

              <div className="hadacy-user-edit__toggles">
                <label
                  className={`hue-toggle ${isAdmin ? "hue-toggle--on" : ""}`}
                >
                  <span className="hue-toggle__dot"></span>
                  Administrador
                  <input
                    type="checkbox"
                    checked={isAdmin}
                    onChange={(e) => setIsAdmin(e.target.checked)}
                  />
                </label>
                <label
                  className={`hue-toggle ${confirmed ? "hue-toggle--on" : ""}`}
                  title="Marca como confirmado para saltarse el flujo de verificación por email"
                >
                  <span className="hue-toggle__dot"></span>
                  Correo verificado
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                  />
                </label>
              </div>

              <div className="hadacy-user-edit__danger-zone">
                <p className="hadacy-user-edit__danger-zone-title">
                  Restablecer contraseña
                </p>
                <p className="hadacy-user-edit__danger-zone-help">
                  Solo úsalo si el cliente no puede recuperar su contraseña.
                  Déjalo vacío para conservar la actual.
                </p>
                <Form.Group controlId="password" className="form-group">
                  <Form.Label>Nueva contraseña</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Mínimo 6 caracteres"
                    value={password}
                    autoComplete="new-password"
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Form.Group>
                <Form.Group controlId="passwordConfirm" className="form-group">
                  <Form.Label>Confirmar nueva contraseña</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Repite la contraseña"
                    value={passwordConfirm}
                    autoComplete="new-password"
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                  />
                </Form.Group>
              </div>

              <button type="submit" className="hadacy-user-edit__cta">
                Guardar cambios <i className="fas fa-arrow-right"></i>
              </button>
            </Form>
          </div>

          {/* ── Read-only metadata ── */}
          <div className="hadacy-user-edit__card">
            <h2 className="hadacy-user-edit__card-title">Información del registro</h2>
            <div className="hadacy-user-edit__meta-row">
              <span className="hadacy-user-edit__meta-label">ID</span>
              <span className="hadacy-user-edit__meta-value">
                {user._id ? `#${String(user._id).slice(-8).toUpperCase()}` : "—"}
              </span>
            </div>
            <div className="hadacy-user-edit__meta-row">
              <span className="hadacy-user-edit__meta-label">Registrado</span>
              <span className="hadacy-user-edit__meta-value">
                {formatDate(user.createdAt)}
              </span>
            </div>
            <div className="hadacy-user-edit__meta-row">
              <span className="hadacy-user-edit__meta-label">Actualizado</span>
              <span className="hadacy-user-edit__meta-value">
                {formatDate(user.updatedAt)}
              </span>
            </div>
            <div className="hadacy-user-edit__meta-row">
              <span className="hadacy-user-edit__meta-label">Email enviado</span>
              <span
                className={`hadacy-user-edit__meta-value ${
                  user.confirmationEmailSent
                    ? "hadacy-user-edit__meta-value--ok"
                    : "hadacy-user-edit__meta-value--err"
                }`}
              >
                {user.confirmationEmailSent ? "Sí" : "No"}
                {user.confirmationEmailSentAt
                  ? ` · ${formatDate(user.confirmationEmailSentAt)}`
                  : ""}
              </span>
            </div>
            {user.confirmationEmailError ? (
              <div className="hadacy-user-edit__meta-error">
                <strong>Error de envío:</strong> {user.confirmationEmailError}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserEditScreen;
